import {
  BALANCE_ERROR,
  hasBalance,
  resolveBalanceError,
  toNumericBalance,
} from 'utils/helper-functions/balance';

// The balance as the maximum limit of a Panoramic 2.0 withdrawal.
//
// The shared balance rules (utils/helper-functions/balance.ts) decide whether
// the amount is blocked. This module decides what the player sees: which
// maximum a limit text shows, and whether the balance, rather than a
// transaction limit, is the maximum the player has broken.

type BalanceValue = number | string | null | undefined;
type AmountValue = number | string | null | undefined;

// Currency code → rate, as carried by the profile's available currencies.
export type CurrencyRates =
  Record<string, { rate?: number | string | null } | undefined> | null | undefined;

export interface BalanceLimitState {
  // Which of the three balance rules is broken, if any (BALANCE_ERROR value).
  balanceErrorType: string | null;
  // True when the balance is the maximum the player has broken: a zero balance
  // with any entered amount, or an amount above a positive balance. The
  // minimum-above-balance rule is a balance error but not a maximum case.
  isBalanceLimitBroken: boolean;
}

export const NO_BALANCE_LIMIT: BalanceLimitState = {
  balanceErrorType: null,
  isBalanceLimitBroken: false,
};

const toFiniteNumber = (value: AmountValue): number | null => {
  if (value === null || value === undefined || value === '') return null;
  const numeric = Number(value);

  return Number.isFinite(numeric) ? numeric : null;
};

/**
 * True when the player has entered an amount. A blank field is not an amount,
 * so it never breaks a balance rule; the validation routines report it as
 * blank before any balance rule runs.
 */
export const hasEnteredAmount = (amount: AmountValue): boolean =>
  amount !== null && amount !== undefined && amount !== '' && amount !== '.';

/**
 * The session balance expressed in another currency, using the profile rates.
 * Returns null when no usable balance was supplied (missing, not zero). When
 * either rate is unknown the balance is used unconverted, as the amount area
 * has always done.
 */
export const convertBalanceToCurrency = ({
  balance,
  balanceCurrency,
  targetCurrency,
  rates,
}: {
  balance: BalanceValue;
  balanceCurrency?: string | null;
  targetCurrency?: string | null;
  rates?: CurrencyRates;
}): number | null => {
  const numericBalance = toNumericBalance(balance);
  if (numericBalance === null) return null;
  if (!balanceCurrency || !targetCurrency || balanceCurrency === targetCurrency)
    return numericBalance;

  const fromRate = Number(rates?.[balanceCurrency]?.rate);
  const toRate = Number(rates?.[targetCurrency]?.rate);
  if (
    !fromRate ||
    !toRate ||
    !Number.isFinite(fromRate) ||
    !Number.isFinite(toRate) ||
    fromRate <= 0
  ) {
    return numericBalance;
  }

  return (numericBalance * toRate) / fromRate;
};

/**
 * The maximum a limit text shows for a withdrawal: the balance, in the limit's
 * currency and rounded down to the cent, whenever it is lower than the
 * transaction maximum (or no transaction maximum is known). Null when the
 * balance does not limit this transaction: a deposit, or no balance.
 */
export const getBalanceMaxForCurrency = ({
  transactionType,
  balance,
  balanceCurrency,
  targetCurrency,
  rates,
}: {
  transactionType?: string | null;
  balance: BalanceValue;
  balanceCurrency?: string | null;
  targetCurrency?: string | null;
  rates?: CurrencyRates;
}): number | null => {
  if (transactionType !== 'pay-out' || !targetCurrency) return null;

  const balanceInTargetCurrency = convertBalanceToCurrency({
    balance,
    balanceCurrency,
    targetCurrency,
    rates,
  });
  if (balanceInTargetCurrency === null || balanceInTargetCurrency < 0) return null;

  return Math.floor(balanceInTargetCurrency * 100) / 100;
};

/**
 * Lowers a limit's maximum to the balance maximum when the balance is lower.
 * The input is never mutated.
 */
export const clampMaxToBalance = <T extends { max_amount?: any }>(
  limits: T,
  balanceMax: number | null,
): T => {
  if (!limits || balanceMax === null) return limits;
  const currentMax = limits.max_amount;
  const numericMax = toFiniteNumber(currentMax);

  if (
    currentMax === null ||
    currentMax === undefined ||
    (numericMax !== null && balanceMax < numericMax)
  ) {
    return { ...limits, max_amount: balanceMax };
  }

  return limits;
};

/**
 * The screen-wide balance-limit state of a withdrawal.
 *
 * `amount` is the entered amount in the base currency and `minimumAmount` the
 * minimum transaction limit in the base currency (null when no minimum
 * applies). The balance is converted into the base currency first. A missing
 * balance, a deposit and a blank amount report nothing.
 */
export const resolveBalanceLimitState = ({
  transactionType,
  balance,
  balanceCurrency,
  baseCurrency,
  amount,
  minimumAmount,
  rates,
}: {
  transactionType?: string | null;
  balance: BalanceValue;
  balanceCurrency?: string | null;
  baseCurrency?: string | null;
  amount: AmountValue;
  minimumAmount?: AmountValue;
  rates?: CurrencyRates;
}): BalanceLimitState => {
  if (transactionType !== 'pay-out' || !hasBalance(balance) || !hasEnteredAmount(amount)) {
    return NO_BALANCE_LIMIT;
  }

  const balanceInBaseCurrency = convertBalanceToCurrency({
    balance,
    balanceCurrency,
    targetCurrency: baseCurrency || balanceCurrency,
    rates,
  });
  if (balanceInBaseCurrency === null) return NO_BALANCE_LIMIT;

  const balanceErrorType = resolveBalanceError({
    balance: balanceInBaseCurrency,
    amount,
    minimumAmount: toFiniteNumber(minimumAmount),
  });
  if (balanceErrorType === null) return NO_BALANCE_LIMIT;

  const numericAmount = toFiniteNumber(amount);
  const isBalanceLimitBroken =
    balanceErrorType === BALANCE_ERROR.INSUFFICIENT ||
    (numericAmount !== null && numericAmount > balanceInBaseCurrency);

  return { balanceErrorType, isBalanceLimitBroken };
};

/**
 * The same distinction for the amount-first layout, read from what its own
 * amount validation already records: the broken rule, and the balance maximum
 * in the currency the validation checked (`validationCurrency`). The entered
 * amount is compared in that same currency.
 */
export const isAmountFirstBalanceLimitBroken = ({
  balanceErrorType,
  balanceMaxAmount,
  validationCurrency,
  baseCurrency,
  amount,
  totalCharge,
}: {
  balanceErrorType: string | null;
  balanceMaxAmount: number | null;
  validationCurrency?: string | null;
  baseCurrency?: string | null;
  amount: AmountValue;
  totalCharge: AmountValue;
}): boolean => {
  if (balanceErrorType === null) return false;
  if (balanceErrorType === BALANCE_ERROR.INSUFFICIENT) return true;
  if (balanceMaxAmount === null) return false;

  const isProcessingCurrency =
    !!validationCurrency && !!baseCurrency && validationCurrency !== baseCurrency;
  const comparedAmount = toFiniteNumber(isProcessingCurrency ? totalCharge : amount);

  return comparedAmount !== null && comparedAmount > balanceMaxAmount;
};

export interface ClassicBalanceChips {
  // Where the insufficient-balance text goes on the classic amount field type.
  baseChip: { showMessage: boolean; hideMaxLimit: boolean };
  processingChip: { showMessage: boolean; hideMaxLimit: boolean };
}

/**
 * The classic amount field type can show one limit text per currency. While
 * the balance limit is broken, the insufficient-balance text goes on the limit
 * text in the balance's currency and the other maximum is hidden, so the
 * message appears once. With a single limit text, that one carries it.
 */
export const resolveClassicBalanceChips = ({
  isBalanceLimitBroken,
  isBaseChipShown,
  isProcessingChipShown,
  balanceCurrency,
  processingCurrency,
}: {
  isBalanceLimitBroken: boolean;
  isBaseChipShown: boolean;
  isProcessingChipShown: boolean;
  balanceCurrency?: string | null;
  processingCurrency?: string | null;
}): ClassicBalanceChips => {
  if (!isBalanceLimitBroken) {
    return {
      baseChip: { showMessage: false, hideMaxLimit: false },
      processingChip: { showMessage: false, hideMaxLimit: false },
    };
  }

  const messageOnBase =
    isBaseChipShown && (!isProcessingChipShown || balanceCurrency !== processingCurrency);

  return {
    baseChip: { showMessage: messageOnBase, hideMaxLimit: !messageOnBase },
    processingChip: { showMessage: !messageOnBase, hideMaxLimit: messageOnBase },
  };
};
